import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AddTagFormData, ItemFilters, SampleTagResponse } from '../types';
import {
  SAMPLE_TYPE_CONFIG,
  SECTION_DEFAULT_VALUES,
  getDefaultFormValues,
} from '../constants';

interface UseSampleTypeResetProps {
  sampleType: ItemFilters;
  isAddTagsOpened: boolean;
  isSubmitted: boolean;
  editMode: boolean;
  duplicateMode: boolean;
  isCreateNewMode: boolean;
  sourceTagData?: SampleTagResponse;
  methods: UseFormReturn<AddTagFormData>;
}

const USER_UPLOAD_IMAGE_TYPES = new Set<ItemFilters>([
  ItemFilters.FIRST_SAMPLE,
  ItemFilters.PROTO_DEVELOPMENT,
  ItemFilters.BLOCK,
]);

const PO_BASED_IMAGE_TYPES = new Set<ItemFilters>([
  ItemFilters.FIT,
  ItemFilters.PRE_PRODUCTION,
  ItemFilters.TOP,
]);

const isUserUploadType = (type: ItemFilters | null): boolean =>
  !!type && USER_UPLOAD_IMAGE_TYPES.has(type);

const isPOBasedType = (type: ItemFilters | null): boolean =>
  !!type && PO_BASED_IMAGE_TYPES.has(type);

const shouldPreserveImageBetweenTypes = (
  oldType: ItemFilters,
  newType: ItemFilters,
): boolean => {
  if (isUserUploadType(oldType) && isUserUploadType(newType)) {
    return true;
  }
  if (isUserUploadType(oldType) && isPOBasedType(newType)) {
    return false;
  }
  if (isPOBasedType(oldType) && isUserUploadType(newType)) {
    return false;
  }
  if (isPOBasedType(oldType) && isPOBasedType(newType)) {
    return true;
  }
  return false;
};

const getImageTransitionValues = (
  currentValues: AddTagFormData,
  shouldPreserveImage: boolean,
): Pick<AddTagFormData, 'productImageUrl' | 'productImageFile'> => {
  if (shouldPreserveImage) {
    if (currentValues.productImageFile) {
      if (
        currentValues.productImageUrl &&
        currentValues.productImageUrl.startsWith('blob:')
      ) {
        URL.revokeObjectURL(currentValues.productImageUrl);
      }
      const result = {
        productImageUrl: URL.createObjectURL(currentValues.productImageFile),
        productImageFile: currentValues.productImageFile,
      };
      return result;
    }
    if (
      currentValues.productImageUrl &&
      currentValues.productImageUrl.trim() !== ''
    ) {
      return {
        productImageUrl: currentValues.productImageUrl,
        productImageFile: undefined,
      };
    }
    return {
      productImageUrl: undefined,
      productImageFile: undefined,
    };
  }

  // Clear images when not preserving (e.g., User-upload → PO-based)
  if (
    currentValues.productImageUrl &&
    currentValues.productImageUrl.startsWith('blob:')
  ) {
    URL.revokeObjectURL(currentValues.productImageUrl);
  }
  return {
    productImageUrl: undefined,
    productImageFile: undefined,
  };
};

/**
 * Custom hook to handle form reset when sample type changes
 * Preserves certain fields while resetting others based on the new sample type configuration
 */
export function useSampleTypeReset({
  sampleType,
  isAddTagsOpened,
  isSubmitted,
  editMode,
  duplicateMode,
  isCreateNewMode,
  sourceTagData,
  methods,
}: UseSampleTypeResetProps) {
  const previousSampleTypeRef = useRef<ItemFilters>(sampleType);
  useEffect(() => {
    if (!isAddTagsOpened || isSubmitted) return;

    if (editMode && !isCreateNewMode && sourceTagData) return;

    const oldType = previousSampleTypeRef.current;

    if (
      duplicateMode &&
      sourceTagData &&
      oldType === ItemFilters.FIRST_SAMPLE &&
      sampleType !== ItemFilters.FIRST_SAMPLE
    ) {
      previousSampleTypeRef.current = sampleType;
      return;
    }

    if (oldType === sampleType) return;

    const currentValues = methods.getValues();
    const updatedValues = { ...currentValues };
    const newConfig = SAMPLE_TYPE_CONFIG[sampleType];

    if (!newConfig) return;

    const newSections = newConfig.sections;
    const shouldPreserveImage = shouldPreserveImageBetweenTypes(
      oldType,
      sampleType,
    );

    // Handle image preservation/clearing
    const imageValues = getImageTransitionValues(
      currentValues,
      shouldPreserveImage,
    );
    Object.assign(updatedValues, imageValues);

    const preservedFields: (keyof AddTagFormData)[] = ['styleNumber'];
    if (shouldPreserveImage) {
      preservedFields.push('productImageFile');
    }

    Object.keys(SECTION_DEFAULT_VALUES).forEach((section) => {
      if (!newSections.includes(section)) {
        const defaultValues = { ...SECTION_DEFAULT_VALUES[section] };

        const fieldsToExclude = shouldPreserveImage
          ? [...preservedFields, 'productImageUrl']
          : preservedFields;
        fieldsToExclude.forEach((field) => {
          if (field in defaultValues) {
            delete (defaultValues as Record<string, unknown>)[field];
          }
        });

        Object.assign(updatedValues, defaultValues);
      }
    });

    const baseDefaults = getDefaultFormValues(sampleType);

    if (
      !updatedValues.fiberContents ||
      !Array.isArray(updatedValues.fiberContents)
    ) {
      updatedValues.fiberContents = baseDefaults.fiberContents;
    }

    methods.reset(updatedValues);
    previousSampleTypeRef.current = sampleType;
  }, [
    sampleType,
    isAddTagsOpened,
    isSubmitted,
    editMode,
    duplicateMode,
    isCreateNewMode,
    sourceTagData,
    methods.reset,
    methods.getValues,
  ]);

  useEffect(() => {
    if (!isAddTagsOpened) {
      previousSampleTypeRef.current = sampleType;
    }
  }, [isAddTagsOpened]);
}
